import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  ConsequenceOfFailure,
  useCofData,
  useConfidenceFromScreening,
  useZoneBreakdownJustificationData,
  useZoneData,
  ZONE_FIELD_IDS,
  ZoneAnalysisConfig,
  ZoneParams,
} from './zoneAnalysisApi'

interface ZoneAnalysisDataContextValue {
  // Zone analysis only consumes the unit/model/inspection surface — pick
  // those fields explicitly so the context isn't broken when
  // `useLibraAssetModelData` is widened for unrelated callers (e.g. the
  // asset-workflow tab layer added `inspectionEntries`, `forceRefresh`,
  // etc.).
  assetModelData: Pick<
    ReturnType<typeof useLibraAssetModelData>,
    | 'unit'
    | 'assetModel'
    | 'siteName'
    | 'components'
    | 'inspections'
    | 'componentDataFrames'
  >
  zones: ZoneParams[]
  setZones: Dispatch<SetStateAction<ZoneParams[]>>
  cof: ConsequenceOfFailure
  setCof: Dispatch<SetStateAction<ConsequenceOfFailure>>
  zoneBreakdownJustification: string | undefined
  setZoneBreakdownJustification: Dispatch<SetStateAction<string | undefined>>
  config: ZoneAnalysisConfig
  confidence: 'low' | 'med' | 'high' // From NII Screening
  hasChanges: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
}

const ZoneAnalysisDataContext = createContext<ZoneAnalysisDataContextValue>({
  assetModelData: {
    unit: undefined,
    assetModel: undefined,
    siteName: '',
    components: [],
    inspections: [],
    componentDataFrames: [],
  },
  zones: [],
  setZones: () => {},
  cof: { level: 'medium', justification: undefined },
  setCof: () => {},
  zoneBreakdownJustification: undefined,
  setZoneBreakdownJustification: () => {},
  config: { zoneFieldName: ZONE_FIELD_IDS.ZONES },
  confidence: 'med',
  hasChanges: false,
  doSave: async () => {},
  resetChanges: () => {},
  isLoading: false,
  isSaving: false,
})

function useZoneDataManagement(fieldName: string): {
  zones: ZoneParams[]
  setZones: Dispatch<SetStateAction<ZoneParams[]>>
  cof: ConsequenceOfFailure
  setCof: Dispatch<SetStateAction<ConsequenceOfFailure>>
  zoneBreakdownJustification: string | undefined
  setZoneBreakdownJustification: Dispatch<SetStateAction<string | undefined>>
  doSave: () => Promise<void>
  hasChanges: boolean
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
} {
  const { isLoading: isLoadingTicketData, zones: ticketZones } =
    useZoneData(fieldName)

  const { isLoading: isLoadingCofData, cof: ticketCof } = useCofData()

  const {
    isLoading: isLoadingJustification,
    zoneBreakdownJustification: ticketJustification,
  } = useZoneBreakdownJustificationData()

  const [zones, setZones] = useState<ZoneParams[]>(ticketZones)
  const [cof, setCof] = useState<ConsequenceOfFailure>(ticketCof)
  const [zoneBreakdownJustification, setZoneBreakdownJustification] = useState<
    string | undefined
  >(ticketJustification)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    // Resync from the ticket, but KEEP unreviewed AI drafts: drafts never
    // persist (see reviewedZones below), so a save writes only reviewed
    // zones back — a plain `setZones(ticketZones)` here would then destroy
    // every proposal still awaiting SME review as collateral of the save.
    // Dedupe by id so a just-confirmed draft (now present in ticketZones)
    // isn't resurrected as a draft copy.
    setZones((prev) => {
      const ticketIds = new Set(ticketZones.map((z) => z.id))
      const pendingDrafts = prev.filter(
        (z) => z.ai_suggested && !ticketIds.has(z.id)
      )
      return [...ticketZones, ...pendingDrafts]
    })
  }, [ticketZones])

  useEffect(() => {
    setCof(ticketCof)
  }, [ticketCof])

  useEffect(() => {
    setZoneBreakdownJustification(ticketJustification)
  }, [ticketJustification])

  // AI suggestions (ai_suggested) are drafts pending SME review: they never
  // persist and never mark the step dirty — only reviewed zones do. This is
  // the data-layer enforcement of "nothing counts until confirmed".
  const reviewedZones = useMemo(
    () => zones.filter((z) => !z.ai_suggested),
    [zones]
  )

  const hasChanges = useMemo(
    () =>
      !isEqual(reviewedZones, ticketZones) ||
      !isEqual(cof, ticketCof) ||
      zoneBreakdownJustification !== ticketJustification,
    [
      reviewedZones,
      ticketZones,
      cof,
      ticketCof,
      zoneBreakdownJustification,
      ticketJustification,
    ]
  )

  const { setFieldValues } = useTicketAccess()
  const doSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await setFieldValues({
        [fieldName]: wrapFieldValue(reviewedZones),
        consequence_of_failure: wrapFieldValue(cof),
        zone_breakdown_justification: wrapFieldValue(
          zoneBreakdownJustification ?? ''
        ),
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    reviewedZones,
    cof,
    zoneBreakdownJustification,
    fieldName,
    setFieldValues,
  ])

  const resetChanges = useCallback(() => {
    setZones(ticketZones)
    setCof(ticketCof)
    setZoneBreakdownJustification(ticketJustification)
  }, [ticketZones, ticketCof, ticketJustification])

  return useMemo(
    () => ({
      zones,
      setZones,
      cof,
      setCof,
      zoneBreakdownJustification,
      setZoneBreakdownJustification,
      doSave,
      hasChanges,
      resetChanges,
      isLoading:
        isLoadingTicketData ||
        isLoadingCofData ||
        isLoadingJustification ||
        isSaving,
      isSaving,
    }),
    [
      zones,
      setZones,
      cof,
      setCof,
      zoneBreakdownJustification,
      setZoneBreakdownJustification,
      doSave,
      hasChanges,
      resetChanges,
      isLoadingTicketData,
      isLoadingCofData,
      isLoadingJustification,
      isSaving,
    ]
  )
}

export const ZoneAnalysisDataProvider: FC<
  PropsWithChildren<{ config: ZoneAnalysisConfig }>
> = ({ children, config }) => {
  const assetModelDataRaw = useLibraAssetModelData()
  const assetModelData = useMemo(
    () => ({
      unit: assetModelDataRaw.unit,
      assetModel: assetModelDataRaw.assetModel,
      siteName: assetModelDataRaw.siteName,
      components: assetModelDataRaw.components,
      inspections: assetModelDataRaw.inspections,
      componentDataFrames: assetModelDataRaw.componentDataFrames,
    }),
    [
      assetModelDataRaw.unit,
      assetModelDataRaw.assetModel,
      assetModelDataRaw.siteName,
      assetModelDataRaw.components,
      assetModelDataRaw.inspections,
      assetModelDataRaw.componentDataFrames,
    ]
  )

  const {
    isLoading: isLoadingZoneData,
    isSaving,
    zones,
    setZones,
    cof,
    setCof,
    zoneBreakdownJustification,
    setZoneBreakdownJustification,
    hasChanges,
    doSave,
    resetChanges,
  } = useZoneDataManagement(config.zoneFieldName)

  // Read confidence from NII Screening step
  const confidence = useConfidenceFromScreening(config.confidenceFieldName)

  const isLoading = isLoadingZoneData || assetModelDataRaw.isLoading

  const value = useMemo(
    () => ({
      assetModelData,
      zones,
      setZones,
      cof,
      setCof,
      zoneBreakdownJustification,
      setZoneBreakdownJustification,
      config,
      confidence,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    }),
    [
      assetModelData,
      zones,
      setZones,
      cof,
      setCof,
      zoneBreakdownJustification,
      setZoneBreakdownJustification,
      config,
      confidence,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    ]
  )

  return (
    <ZoneAnalysisDataContext.Provider value={value}>
      {children}
    </ZoneAnalysisDataContext.Provider>
  )
}

export const useZoneAnalysisData = () => {
  const context = useContext(ZoneAnalysisDataContext)
  if (!context) {
    throw new Error(
      'useZoneAnalysisData must be used within ZoneAnalysisDataProvider'
    )
  }
  return context
}
